import React, { useState, useEffect } from 'react';
import MiniCalendar from './MiniCalendar';
import { FaVideo } from 'react-icons/fa';
import { BsArrowClockwise, BsCalendar } from 'react-icons/bs';
import { TbLayoutSidebarRightCollapse, TbLayoutSidebarRightExpand } from 'react-icons/tb';
import '../Styles/App.css';
import '../Styles/RightSidebar.css';
import useCalendar from '../hooks/useCalendar';
import configDetails from '../Config/Config';
import googleCalendarIcon from '../Images/googlecalender.png';
import outlookIcon from '../Images/outlook.png';

const {
  baseUrl,
  NOTIFY_MEETINGS
} = configDetails;

function RightSidebar({ show, onToggle, onClose }) {
  const [activeTab, setActiveTab] = useState('Meetings');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notification, setNotification] = useState(null);

  // Integration with useCalendar hook
  const {
    eventsLoading, eventsError, calendarConnected, outlookConnected, outlookNeedsReauth, mergedEvents, anyCalendarConnected,
    fetchCalendarEvents: fetchGoogleCalendarEvents, fetchOutlookCalendarEvents, connectGoogleCalendar, connectOutlookCalendar,
    switchToGoogleCalendar, switchToOutlookCalendar,
    selectedDate, setSelectedDate, eventsForDate, todayEvents, upcomingEvents, datesWithEvents,
  } = useCalendar({ activeTab: 'Calendar', setNotification });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && show) {
      document.body.classList.add('sidebar-overlay-open');
    } else {
      document.body.classList.remove('sidebar-overlay-open');
    }
    
    return () => {
      document.body.classList.remove('sidebar-overlay-open');
    };
  }, [isMobile, show]);

  useEffect(() => {
    const savedEvents = localStorage.getItem('calendarEvents');
    if (savedEvents) {
      setCalendarEvents(JSON.parse(savedEvents));
    }
  }, []);

  // Fetch calendar events (shared between auto-refresh + manual refresh)
  const fetchCalendarEvents = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      setIsRefreshing(false);
      return;
    }

    try {
      const timeMin = new Date().toISOString();
      const url = `${configDetails.calendarApiUrl}?maxResults=10&orderBy=startTime&singleEvents=true&timeMin=${timeMin}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error('Unauthorized or error fetching events');
      }

      const data = await res.json();
      setCalendarEvents(data.items || []);
      localStorage.setItem('calendarEvents', JSON.stringify(data.items || []));
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const isValidUrl = (url) => {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch (_) {
      return false;
    }
  };

  const handleRecordClick = async () => {
    if (!isValidUrl(meetingUrl)) {
      alert('Please enter a valid meeting URL.');
      return;
    }

    try {
      const response = await fetch(`${baseUrl}${NOTIFY_MEETINGS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingUrl: meetingUrl,
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setMeetingUrl('');
        alert('Recording will start once the host/admin allows it.');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to notify backend'}`);
      }
    } catch (error) {
      console.error('API call failed:', error);
      alert('An error occurred while contacting the server.');
    }
  };


  return (
    <>
      {/* Overlay for mobile when sidebar is open */}
      {isMobile && show && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
        />
      )}
      
      <div
        className={`sidebar-container ${isMobile ? 'mobile' : 'desktop'} ${show ? 'show' : 'hidden'}`}
      >
      {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 fw-bold">Meetings</h5>
          <button
            onClick={isMobile ? onClose : onToggle}
            className="header-toggle-btn"
            title={show ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-label={show ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {show ? (
              <TbLayoutSidebarRightCollapse size={24} />
            ) : (
              <TbLayoutSidebarRightExpand size={24} />
            )}
          </button>
        </div>

        {/* Scrollable body - only this part scrolls now, not the whole sidebar */}
        <div className="sidebar-scroll-body">

        {/* Meetings Tab */}
        {activeTab === 'Meetings' && (
          <>
            <div className="sidebar-fixed-header">
            <h6 className="fw-bold mb-2">Record a live meeting</h6>
            <p className="text-muted small mb-3">Works with Zoom, Google Meet</p>
            <div className="meeting-url-field mb-4">
              <FaVideo size={16} className="meeting-url-icon" />
              <input
                type="text"
                placeholder="Paste meeting URL to add Noteforge"
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
              />
              {meetingUrl.trim() !== '' && (
                <button className="record-btn" onClick={handleRecordClick}>
                  Record
                </button>
              )}
            </div>

            <h6 className="fw-bold mb-2">Record scheduled meetings</h6>

            {/* Calendar Connection UI */}
            {!anyCalendarConnected ? (
              /* Show both connect buttons when nothing is connected */
              <div className="d-flex flex-column gap-2">
                <button
                  className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 w-100"
                  onClick={connectGoogleCalendar}
                >
                  <img src={googleCalendarIcon} alt="Google" style={{ width: '18px', height: '18px' }} />
                  Connect Google Calendar
                </button>
                <button
                  className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 w-100"
                  onClick={connectOutlookCalendar}
                >
                  <img src={outlookIcon} alt="Outlook" style={{ width: '18px', height: '18px' }} />
                  Connect Outlook Calendar
                </button>
              </div>
            ) : (
              /* Show toggle once any calendar is connected */
              <div className="calendar-toggle-wrapper">
                <p className="small text-muted mb-2">Connected calendar</p>
                <div className="calendar-provider-toggle">
                  {/* Google toggle button */}
                  <button
                    className={`calendar-toggle-btn ${calendarConnected ? 'active' : ''}`}
                    onClick={calendarConnected ? undefined : switchToGoogleCalendar}
                    title={calendarConnected ? 'Google Calendar connected' : 'Switch to Google Calendar'}
                  >
                    <img src={googleCalendarIcon} alt="Google Calendar" style={{ width: '20px', height: '20px' }} />
                    <span>Google</span>
                    {calendarConnected && (
                      <span className="toggle-active-dot" />
                    )}
                  </button>

                  {/* Divider */}
                  <div className="toggle-divider" />

                  {/* Outlook toggle button */}
                  <button
                    className={`calendar-toggle-btn ${outlookConnected ? 'active' : ''}`}
                    onClick={outlookConnected ? undefined : switchToOutlookCalendar}
                    title={outlookConnected ? 'Outlook Calendar connected' : 'Switch to Outlook Calendar'}
                  >
                    <img src={outlookIcon} alt="Outlook" style={{ width: '20px', height: '20px' }} />
                    <span>Outlook</span>
                    {outlookConnected && (
                      <span className="toggle-active-dot" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {outlookNeedsReauth && (
              <div
                className="d-flex align-items-center justify-content-between gap-2 mt-2 p-2 rounded"
                style={{ background: '#fff3cd', border: '1px solid #ffe69c' }}
              >
                <span className="small" style={{ color: '#664d03' }}>
                  Outlook Calendar needs to be reconnected.
                </span>
                <button
                  className="btn btn-sm btn-outline-primary flex-shrink-0"
                  onClick={connectOutlookCalendar}
                >
                  Reconnect
                </button>
              </div>
            )}

            </div>
            {/* end sidebar-fixed-header */}

            {/* Sync bar - calendar pill + sync button stays visible */}
            {anyCalendarConnected && (
              <div className="sidebar-sync-bar">
                <div className="mt-4 pt-3 border-top">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    {/* "Calendar ↓" pill trigger */}
                    <button
                      className={`cal-dropdown-trigger ${calendarOpen ? 'open' : ''}`}
                      onClick={() => setCalendarOpen(o => !o)}
                      aria-expanded={calendarOpen}
                    >
                      <BsCalendar size={14} />
                      <span>
                        {calendarOpen
                          ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'Calendar'}
                      </span>
                      <svg
                        className={`cal-chevron ${calendarOpen ? 'rotated' : ''}`}
                        width="12" height="12" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {/* Sync button */}
                    <button
                      className="btn btn-sm text-muted p-1"
                      style={{ border: 'none', background: 'none', lineHeight: 1 }}
                      title="Sync calendar"
                      onClick={async () => {
                        setIsRefreshing(true);
                        try {
                          if (calendarConnected) await fetchGoogleCalendarEvents(null, false);
                          if (outlookConnected) await fetchOutlookCalendarEvents(false);
                        } finally {
                          setIsRefreshing(false);
                        }
                      }}
                    >
                      <BsArrowClockwise size={16} className={isRefreshing ? 'spin' : ''} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="sidebar-events-scroll">
            {/* Calendar events */}
            {calendarEvents.length > 0 && (
              <div className="mt-4">

                {/* Title + Refresh icon */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-2">Upcoming Meetings</h6>
                  <button
                    className="ui-btn ui-corner-all ui-btn-icon-notext"
                    onClick={fetchCalendarEvents}
                    title="Refresh Events"
                  >
                    <BsArrowClockwise className={isRefreshing ? 'spin' : ''} />
                  </button>
                </div>

                <div className="thin-scrollbar events-scroll-container">
                  {calendarEvents
                    .filter((event) => {
                      const eventDate = new Date(event.start.dateTime || event.start.date);
                      return eventDate >= new Date();
                    })
                    .map((event, idx) => {
                      const meetLink = event.conferenceData?.entryPoints?.find((e) => e.entryPointType === 'video')?.uri;
                      return (
                        <div
                          key={idx}
                          className="d-flex align-items-center justify-content-between shadow-sm rounded border mb-3 p-3 bg-white"
                        >
                          <div className="d-flex flex-column">
                            <strong className="text-dark fs-6">{event.summary || 'Untitled Event'}</strong>
                            <span className="text-muted small">
                              <i className="fa-regular fa-clock me-1"></i>
                              {new Intl.DateTimeFormat('en-US', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                                timeZone: 'Asia/Kolkata',
                              }).format(new Date(event.start.dateTime || event.start.date))}
                            </span>
                            {event.location && (
                              <span className="text-muted small">
                                <i className="fa-solid fa-location-dot me-1"></i>
                                {event.location}
                              </span>
                            )}
                            {event.htmlLink && (
                              <a
                                href={event.htmlLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary small mt-1"
                              >
                                Open in Google Calendar
                              </a>
                            )}
                            {meetLink && (
                              <a
                                href={meetLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-success mt-2"
                              >
                                Join Meet
                              </a>
                            )}
                          </div>
                          <div className="form-check form-switch ms-3">
                            <input className="form-check-input" type="checkbox" defaultChecked />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Merged Calendar Events - date-filtered, Otter-style */}
            {anyCalendarConnected && (
              <div>

                {/* Blue progress bar — only visible while syncing */}
                {isRefreshing && (
                  <div style={{
                    height: '2px',
                    borderRadius: '1px',
                    overflow: 'hidden',
                    marginBottom: '4px',
                  }}>
                    <div style={{
                      height: '100%',
                      background: '#0d6efd',
                      animation: 'calSyncProgress 1.2s ease-in-out infinite',
                    }} />
                  </div>
                )}

                {/* Collapsible calendar panel */}
                {calendarOpen && (
                  <div className="cal-dropdown-panel">
                    <MiniCalendar
                      value={selectedDate}
                      onChange={(date) => {
                        setSelectedDate(date);
                        setCalendarOpen(false);
                      }}
                      datesWithEvents={datesWithEvents}
                    />
                  </div>
                )}

                {/* Events grouped by actual date */}
                {eventsLoading ? (
                  <p className="text-muted small text-center py-2">Loading events…</p>
                ) : (() => {
                  // Helper: render a single event card
                  const renderEventCard = (event, idx) => {
                    const meetLink = event.hangoutLink ||
                      event.conferenceData?.entryPoints?.find(p => p.entryPointType === 'video')?.uri;

                    const startTimeLabel = new Date(event.start.dateTime).toLocaleString('en-US', {
                      hour: 'numeric', minute: '2-digit', hour12: true,
                      timeZone: 'Asia/Kolkata',
                    });

                    const eventStart = new Date(event.start.dateTime);
                    const eventEnd = event.end?.dateTime ? new Date(event.end.dateTime) : null;
                    const now = new Date();
                    const isLive = !!eventEnd && now >= eventStart && now <= eventEnd;

                    return (
                      <div
                        key={event.id || idx}
                        className="calendar-event-card calendar-event-row d-flex align-items-center justify-content-between shadow-sm rounded border mb-2 p-3 bg-white"
                      >
                        <div className="calendar-event-info">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h6 className="mb-0 fw-bold text-dark">{event.summary || 'Untitled Event'}</h6>
                            {isLive && (
                              <span className="live-indicator live-indicator--live" aria-label="Live now">
                                <span className="live-dot" />
                                <span className="live-waveform">
                                  <span className="live-wave-bar" />
                                  <span className="live-wave-bar" />
                                  <span className="live-wave-bar" />
                                  <span className="live-wave-bar" />
                                </span>
                              </span>
                            )}
                          </div>
                          <span className="small text-muted">{startTimeLabel}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {!isLive && (
                            <span className="live-indicator live-indicator--static" aria-label="Upcoming">
                              <span className="live-dot" />
                            </span>
                          )}
                          {meetLink ? (
                            <a
                              href={meetLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="join-meeting-link"
                            >
                              Join
                            </a>
                          ) : (
                            <span className="join-meeting-link join-meeting-link--disabled">
                              No link
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  };

                  // Helper: format a date the way Otter does — "Today, Jul 8" / "Fri, Jul 10"
                  const formatDateHeader = (date) => {
                    const today = new Date();
                    const isToday = date.getFullYear() === today.getFullYear() &&
                      date.getMonth() === today.getMonth() &&
                      date.getDate() === today.getDate();
                    const label = isToday
                      ? `Today, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                      : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return { label, isToday };
                  };

                  // Helper: group an event list by date and render with headers
                  const renderGroupedEvents = (eventList) => {
                    const groups = [];
                    const seen = new Map();
                    eventList.forEach(event => {
                      const d = new Date(event.start.dateTime);
                      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                      if (!seen.has(key)) {
                        seen.set(key, groups.length);
                        groups.push({ date: d, key, events: [] });
                      }
                      groups[seen.get(key)].events.push(event);
                    });
                    return groups.map(({ date, key, events: groupEvents }) => {
                      const { label, isToday } = formatDateHeader(date);
                      return (
                        <div key={key} className="mb-3">
                          <p className={`event-date-header mb-2 ${isToday ? 'event-date-header--today' : ''}`}>
                            {label}
                          </p>
                          {groupEvents.map((event, idx) => renderEventCard(event, idx))}
                        </div>
                      );
                    });
                  };

                  const hasTodayEvents = todayEvents.length > 0;
                  const hasUpcomingEvents = upcomingEvents.length > 0;
                  const today = new Date();
                  const sel = selectedDate || today;
                  const { label: selectedDayLabel, isToday: isSelectedDayToday } = formatDateHeader(sel);

                  // Neither selected-day nor upcoming
                  if (!hasTodayEvents && !hasUpcomingEvents) {
                    const dateLabel = isSelectedDayToday ? 'today' : sel.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    return (
                      <div className="text-center py-3">
                        <BsCalendar size={22} className="text-muted mb-2" />
                        <p className="text-muted small mb-0">No events on {dateLabel}</p>
                        <p className="text-muted small">No upcoming events either</p>
                      </div>
                    );
                  }

                  return (
                    <div className="mt-3">
                      {/* Selected-day events section */}
                      <p className={`event-date-header mb-2 ${isSelectedDayToday ? 'event-date-header--today' : ''}`}>
                        {selectedDayLabel}
                      </p>
                      {hasTodayEvents
                        ? todayEvents.map((event, idx) => renderEventCard(event, idx))
                        : <p className="text-muted small mb-3">
                            No events scheduled for {isSelectedDayToday ? 'today' : sel.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                      }

                      {/* Upcoming events section */}
                      {hasUpcomingEvents && renderGroupedEvents(upcomingEvents)}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          {/* end sidebar-events-scroll */}
          </>
        )}

        </div>
        {/* end sidebar-scroll-body */}

      </div>
    </>
  );
}

export default RightSidebar;